import { Check, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { CodeBlock } from '@/components/audit/RunDetail'
import { DataTable, type Column } from '@/components/data/DataTable'
import { DetailPane, DetailSection, KeyValue, detailDangerClass } from '@/components/data/DetailPane'
import { LoadingRows } from '@/components/data/SearchInput'
import { SplitView } from '@/components/data/SplitView'
import { SectionBoundary } from '@/components/feedback/SectionBoundary'
import { Page } from '@/components/layout/Page'
import { decideApproval, fetchApprovals, type ApprovalRequest } from '@/lib/approvals'
import { useSelection } from '@/lib/useSelection'

function relativeAge(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function relativeIn(iso: string): string {
  const mins = Math.floor((new Date(iso).getTime() - Date.now()) / 60_000)
  if (mins <= 0) return 'expired'
  if (mins < 60) return `in ${mins}m`
  const hrs = Math.floor(mins / 60)
  return hrs < 24 ? `in ${hrs}h` : `in ${Math.floor(hrs / 24)}d`
}

const paramsSummary = (p: Record<string, unknown>) =>
  Object.entries(p ?? {}).map(([k, v]) => `${k}: ${Array.isArray(v) ? `[${v.length}]` : String(v).slice(0, 40)}`).join(' · ')

const COLUMNS: Column<ApprovalRequest>[] = [
  {
    key: 'requested_at', header: 'Requested', mono: true, width: '110px',
    render: (r) => relativeAge(r.requested_at), title: (r) => new Date(r.requested_at).toLocaleString(),
  },
  { key: 'operation', header: 'Operation', mono: true, width: '30%' },
  { key: 'requested_by', header: 'Requested by', width: '22%' },
  {
    key: 'parameters', header: 'Parameters', mono: true,
    render: (r) => <span className="text-text-2">{paramsSummary(r.parameters) || '—'}</span>,
    title: (r) => paramsSummary(r.parameters) || undefined,
  },
  { key: 'expires_at', header: 'Expires', mono: true, width: '96px', render: (r) => relativeIn(r.expires_at) },
]

const approveClass =
  'inline-flex items-center gap-1.5 rounded-md border border-primary bg-primary px-2.5 py-1 text-[12px] font-semibold text-accent-fg transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-60'

function ApprovalDetail({ req, onDecide }: {
  req: ApprovalRequest
  onDecide: (id: string, decision: 'approve' | 'reject') => Promise<void>
}) {
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null)
  async function handle(decision: 'approve' | 'reject') {
    setBusy(decision)
    try {
      await onDecide(req.id, decision)
    } finally {
      setBusy(null)
    }
  }
  return (
    <DetailPane
      eyebrow="Approval request"
      title={<span className="font-mono">{req.operation}</span>}
      status={<span className="font-medium text-warn">{req.status}</span>}
      actions={(
        <>
          <button type="button" className={approveClass} disabled={busy !== null} onClick={() => void handle('approve')}>
            <Check aria-hidden className="size-3.5" /> {busy === 'approve' ? 'Approving…' : 'Approve'}
          </button>
          <button type="button" className={detailDangerClass} disabled={busy !== null} onClick={() => void handle('reject')}>
            <X aria-hidden className="size-3.5" /> {busy === 'reject' ? 'Rejecting…' : 'Reject'}
          </button>
        </>
      )}
    >
      <KeyValue
        items={[
          ['Requested by', req.requested_by],
          ['Requested', <span className="font-mono text-[12px]">{new Date(req.requested_at).toLocaleString()}</span>],
          ['Expires', <span className="font-mono text-[12px]">{new Date(req.expires_at).toLocaleString()}</span>],
          ['Request', <span className="font-mono text-[12px] text-text-2">{req.id}</span>],
        ]}
      />
      <DetailSection title="Parameters">
        <CodeBlock>{JSON.stringify(req.parameters ?? {}, null, 2)}</CodeBlock>
      </DetailSection>
      <p className="text-[12px] text-text-3">
        Four-eyes gate: a different approver or owner must approve before the operation runs.
      </p>
    </DetailPane>
  )
}

export default function ApprovalsPage() {
  const [sel, setSel] = useSelection()
  const [requests, setRequests] = useState<ApprovalRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionMsg, setActionMsg] = useState<{ ok: boolean; text: string } | null>(null)

  const refresh = useCallback(() => {
    setLoading(true)
    setError(null)
    fetchApprovals()
      .then((r) => setRequests(Array.isArray(r) ? r : []))
      .catch((e: Error) => {
        const is403 = e.message.startsWith('403')
        setError(is403 ? 'Only approvers and owners can view pending approvals.' : `Failed to load: ${e.message}`)
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const onDecide = useCallback(
    async (id: string, decision: 'approve' | 'reject') => {
      setActionMsg(null)
      try {
        await decideApproval(id, decision)
        setActionMsg({ ok: true, text: decision === 'approve' ? 'Operation approved and executed.' : 'Request rejected.' })
        setSel(null)
        refresh()
      } catch (e: unknown) {
        const msg = (e as Error).message
        setActionMsg({
          ok: false,
          text: msg.startsWith('403')
            ? 'Only approvers/owners can decide. Self-approval is not permitted.'
            : `Error: ${msg}`,
        })
      }
    },
    [refresh, setSel],
  )

  const selected = requests.find((r) => r.id === sel) ?? null

  return (
    <Page
      title="Approvals"
      hint="Four-eyes gate: critical operations requested by operators must be approved by a different approver or owner before execution."
      freshness={!loading && !error ? <span className="font-mono">{requests.length} pending</span> : undefined}
    >
      {actionMsg && (
        <p
          role={actionMsg.ok ? 'status' : 'alert'}
          className={`shrink-0 border-b border-line px-4 py-1.5 text-[12px] ${actionMsg.ok ? 'text-ok' : 'text-danger'}`}
        >
          {actionMsg.text}
        </p>
      )}
      {loading
        ? <LoadingRows label="Loading approvals" />
        : error
          ? <p role="alert" className="px-4 py-4 text-[13px] text-danger">{error}</p>
          : (
            <SplitView
              onCloseDetail={() => setSel(null)}
              emptyDetail="Select a request to review it."
              detail={selected && (
                <SectionBoundary label="Approval request" resetKeys={[selected]}>
                  <ApprovalDetail req={selected} onDecide={onDecide} />
                </SectionBoundary>
              )}
              list={(
                <DataTable
                  label="Pending approvals"
                  rows={requests}
                  columns={COLUMNS}
                  rowId={(r) => r.id}
                  selectedId={sel}
                  onSelect={setSel}
                  empty="No pending approvals."
                />
              )}
            />
          )}
    </Page>
  )
}
